/**
 * Thin fetch wrapper for the DeployIt API. Lives in the SPA only — the CLI uses
 * the oapi-codegen-generated Go client. Both target the same /api/v1 surface.
 *
 * Conventions:
 * - All requests carry credentials (the session cookie is set by /auth/login).
 * - JSON in / JSON out.
 * - Errors throw `ApiError` with the parsed `{ error: { code, message } }` payload.
 */

export interface PrincipalResponse {
  principal: { type: 'user' | 'serviceAccount'; id: string };
  user: {
    id: string;
    email: string;
    name: string;
    emailVerified: boolean;
    createdAt: string;
  } | null;
  memberships: Array<{
    orgId: string;
    orgSlug: string;
    orgName: string;
    orgPath: string;
  }>;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details: unknown;

  constructor(status: number, payload: { error: { code: string; message: string; details?: unknown } }) {
    super(payload.error.message);
    this.status = status;
    this.code = payload.error.code;
    this.details = payload.error.details;
  }
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json' },
  };
  if (body !== undefined) {
    init.headers = { ...init.headers, 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(path, init);
  if (res.status === 204) return undefined as T;
  let json: unknown;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(res.status, {
      error: { code: 'invalid_response', message: `HTTP ${res.status} ${res.statusText}` },
    });
  }
  if (!res.ok) {
    throw new ApiError(res.status, json as { error: { code: string; message: string } });
  }
  return json as T;
}

/**
 * Currently empty — reserved for future instance-wide settings the global
 * login page needs. Per-org SSO is exposed by per-org endpoints, not here.
 */
export type AuthConfigResponse = Record<string, never>;

/**
 * Discriminated /auth/login response. See the API doc comment for the route.
 *
 * Backward-compatibility note: existing callers that used to do
 * `const p = await login(...); console.log(p.user)` keep working unchanged
 * because LoginOkResponse carries the same `user`/`memberships`/`principal`
 * shape as PrincipalResponse, just with an extra `status: 'ok'` tag.
 */
export type LoginResponse =
  | (PrincipalResponse & { status: 'ok' })
  | {
      status: 'password-change-required';
      challengeToken: string;
      user: { id: string; email: string; name: string };
    }
  | {
      status: 'two-factor-required';
      challengeToken: string;
      user: { id: string; email: string; name: string };
    };

export interface TwoFactorStatusResponse {
  enabled: boolean;
  enabledAt: string | null;
  recoveryCodesRemaining: number;
}

export interface TwoFactorEnrollResponse {
  secret: string;
  otpauthUri: string;
  qrSvg: string;
}

export interface TwoFactorActivateResponse {
  enabled: true;
  recoveryCodes: string[];
}

export interface Organisation {
  id: string;
  name: string;
  slug: string;
  parentOrgId: string | null;
  path: string;
  createdAt: string;
}

export interface CreateOrganisationResponse {
  organisation: Organisation;
  /**
   * Present only when the create payload supplied `adminEmail`. When null, the
   * org has no direct members and is administered via cascade from any
   * ancestor's `core.org.admin` holders.
   */
  invitation: {
    invitationId: string;
    email: string;
    acceptUrl: string;
    expiresAt: string;
  } | null;
}

export interface InvitationDescription {
  email: string;
  organisation: { id: string; name: string; slug: string; path: string };
  invitedBy: { name: string; email: string };
  expiresAt: string;
  /** Whether the invitation email is already attached to a DeployIt user. */
  emailKnown: boolean;
}

export interface AcceptInvitationResponse {
  user: { id: string; email: string; name: string };
  organisation: { id: string; name: string; slug: string; path: string };
  /** True if the membership was attached to an existing user; false if a new user was created. */
  attached: boolean;
}

export type AcceptInvitationInput =
  | { mode: 'new'; name: string; password: string }
  | { mode: 'attach' };

export interface AccountEmail {
  id: string;
  email: string;
  isPrimary: boolean;
  verified: boolean;
  source: 'signup' | 'invitation' | 'manual' | 'oauth';
  createdAt: string;
}

export interface OrgMember {
  membershipId: string;
  userId: string;
  userEmail: string;
  userName: string;
  grantedPermissions: string[];
  joinedAt: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  invitedByUserId: string;
  invitedByName: string;
  grantedPermissions: string[];
  expiresAt: string;
  createdAt: string;
}

export interface InviteMemberResponse {
  invitationId: string;
  email: string;
  acceptUrl: string;
  expiresAt: string;
}

export type SsoProvider = 'google' | 'github';

export interface OrgSsoConfig {
  id: string;
  orgId: string;
  provider: SsoProvider;
  clientIdLast4: string;
  matcher: { workspaceDomain?: string; organizationLogin?: string };
  defaultGrantedPermissions: string[];
  autoProvision: boolean;
  enabled: boolean;
  hasClientSecret: boolean;
  startUrl: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSsoConfigInput {
  provider: SsoProvider;
  clientId: string;
  clientSecret: string;
  matcher: { workspaceDomain?: string } | { organizationLogin?: string };
  defaultGrantedPermissions?: string[];
  autoProvision?: boolean;
  enabled?: boolean;
}

export interface UpdateSsoConfigInput {
  clientId?: string;
  clientSecret?: string;
  matcher?: { workspaceDomain?: string } | { organizationLogin?: string };
  defaultGrantedPermissions?: string[];
  autoProvision?: boolean;
  enabled?: boolean;
}

export interface PublicSsoProviders {
  organisation: { slug: string; name: string };
  providers: Array<{ provider: SsoProvider; startUrl: string }>;
}

export type AuditOutcome = 'success' | 'error' | 'partial' | 'cancelled';
export type AuditTargetType = 'org' | 'application' | 'component' | 'script' | 'backup' | 'monitor';

export interface AuditEntry {
  id: string;
  actor: { type: 'user' | 'serviceAccount'; id: string; label: string };
  via: string;
  orgId: string;
  action: string;
  targetType: AuditTargetType;
  targetId: string;
  inputs: unknown;
  result: unknown;
  outcome: AuditOutcome;
  errorMessage: string | null;
  durationMs: number;
  timestamp: string;
}

export interface AuditQuery {
  before?: string;
  actionPrefix?: string;
  limit?: number;
}

export type RepositoryTopology = 'single' | 'polyrepo' | 'monorepo' | 'unknown';

export interface Application {
  id: string;
  orgId: string;
  orgSlug: string;
  orgName: string;
  name: string;
  slug: string;
  description: string | null;
  templateId: string | null;
  repositoryTopology: RepositoryTopology;
  createdAt: string;
  updatedAt: string;
}

export interface CreateApplicationInput {
  name: string;
  slug?: string;
  description?: string;
  templateId?: string;
}

export interface UpdateApplicationInput {
  name?: string;
  description?: string | null;
  templateId?: string | null;
  repositoryTopology?: RepositoryTopology;
}

/* ------------- Discovery ------------- */

/**
 * Mirror of the server's reconciler types. Kept structural (not as a
 * discriminated union here) because the SPA narrows on `.kind` per case.
 */
export type DiscoveryProposal =
  | {
      kind: 'create';
      componentSlug: string;
      componentId: string;
      sourceFile: string;
      spec: YamlScriptShape;
    }
  | {
      kind: 'update';
      componentSlug: string;
      componentId: string;
      sourceFile: string;
      scriptId: string;
      existing: Script;
      spec: YamlScriptShape;
      changedFields: string[];
    }
  | {
      kind: 'noop';
      componentSlug: string;
      componentId: string;
      sourceFile: string;
      scriptId: string;
      spec: YamlScriptShape;
    }
  | {
      kind: 'remove';
      componentSlug: string;
      componentId: string;
      sourceFile: string;
      scriptId: string;
      existing: Script;
    }
  | {
      /**
       * `.deployit/components/<slug>/scripts.yaml` references a Component
       * slug that doesn't exist on the application. Proposal to create
       * the Component (role defaults to 'other'); scripts under it land
       * as ordinary `create` proposals on the next /discover pass once
       * the row exists.
       */
      kind: 'create-component';
      componentSlug: string;
      sourceFile: string;
      suggestedRole:
        | 'web'
        | 'database'
        | 'files'
        | 'cache'
        | 'queue'
        | 'search'
        | 'worker'
        | 'repo'
        | 'other';
      suggestedName: string;
      pendingScriptCount: number;
    }
  | {
      /**
       * `.deployit/app.yaml -> deployTarget.host` doesn't match any
       * existing Server in the org. Operator ticks this to create the
       * Server row.
       */
      kind: 'create-server';
      sourceFile: string;
      orgId: string;
      spec: {
        name: string;
        slug: string;
        hostname: string;
      };
    }
  | {
      /**
       * Discovery wants an access method that doesn't exist. Two flavours:
       *
       *   1. SSH leaf from `.deployit/app.yaml -> deployTarget`. transport=null.
       *   2. Docker / kubectl wrapper from `.deployit/components/<slug>/component.yaml`
       *      runtime hint. transport references the SSH leaf via existingId
       *      or pendingProposalKey.
       */
      kind: 'create-access-method';
      sourceFile: string;
      orgId: string;
      componentSlug?: string;
      spec: {
        name: string;
        slug: string;
        kind: 'ssh' | 'docker' | 'kubectl' | 'local';
        settings: Record<string, unknown>;
        description?: string;
      };
      transport:
        | null
        | { existingId: string }
        | { pendingProposalKey: string };
      proposalKey: string;
    };

/**
 * Defaulted YAML script — same shape as the server's `YamlScript`. We don't
 * import from `@deployit/plugin-applications` to keep the SPA bundle clear
 * of the server-side parser; this is the structural twin.
 */
export interface YamlScriptShape {
  slug: string;
  name: string;
  description?: string;
  commandTemplate: string;
  executionMode: ScriptExecutionMode;
  expectedDurationMs?: number;
  syncTimeoutMs?: number;
  queuedTimeoutMs?: number;
  reversibility: ScriptReversibility;
  warnings: string[];
  paramSchema?: unknown;
  requiredPermission: string;
  environments?: AllowedEnvironments | null;
}

/**
 * Active pending-discovery row for an application — surfaced by the
 * banner on the application detail page. Null when nothing's pending.
 * The `proposals` shape is the same `DiscoveryProposal[]` the dialog
 * renders.
 */
export interface PendingDiscovery {
  id: string;
  applicationId: string;
  repoComponentId: string;
  fetchedSha: string;
  proposals: DiscoveryProposal[];
  detectedAt: string;
}

export interface DiscoveryReport {
  applicationId: string;
  proposals: DiscoveryProposal[];
  warnings: Array<{ message: string; filePath?: string }>;
  errors: Array<{
    filePath: string;
    kind: 'yaml' | 'schema' | 'io';
    message: string;
    componentSlug?: string;
  }>;
  totals: {
    create: number;
    update: number;
    noop: number;
    remove: number;
    createComponent: number;
    createServer: number;
    createAccessMethod: number;
    warnings: number;
    errors: number;
  };
}

export type DiscoverySelection =
  | { componentSlug: string; scriptSlug: string; action: 'create' }
  | { componentSlug: string; scriptSlug: string; action: 'update' }
  | { componentSlug: string; scriptSlug: string; action: 'remove' }
  | { componentSlug: string; action: 'create-component' }
  | { serverSlug: string; action: 'create-server' }
  | { accessMethodSlug: string; action: 'create-access-method' };

export interface DiscoveryApplyResult {
  applicationId: string;
  results: Array<{
    /** Present for script + create-component actions. */
    componentSlug?: string;
    /** Present for script-level actions only. */
    scriptSlug?: string;
    /** Present for create-server. */
    serverSlug?: string;
    /** Present for create-access-method. */
    accessMethodSlug?: string;
    action:
      | 'create'
      | 'update'
      | 'remove'
      | 'create-component'
      | 'create-server'
      | 'create-access-method';
    outcome: 'applied' | 'skipped' | 'error';
    scriptId?: string;
    /** Set when a `create-component` action applies successfully. */
    componentId?: string;
    serverId?: string;
    accessMethodId?: string;
    reason?: string;
  }>;
  totals: { applied: number; skipped: number; errors: number };
}

export type EnvironmentKind = 'production' | 'staging' | 'development' | 'preview' | 'other';

export interface Environment {
  id: string;
  applicationId: string;
  name: string;
  slug: string;
  kind: EnvironmentKind;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEnvironmentInput {
  name: string;
  slug?: string;
  kind: EnvironmentKind;
  description?: string;
}

export interface UpdateEnvironmentInput {
  name?: string;
  description?: string | null;
}

export type ComponentRole =
  | 'web'
  | 'database'
  | 'files'
  | 'cache'
  | 'queue'
  | 'search'
  | 'worker'
  /** Source-code repository — drives discovery. See issue #41. */
  | 'repo'
  | 'other';

export type ScmKind = 'github' | 'gitlab' | 'bitbucket';

/**
 * Settings shape for `components.role === 'repo'`. Mirror of the server-side
 * `RepoComponentSettingsInput` in `@deployit/plugin-applications`. Platform-
 * managed fields (`webhookSecret`, `webhookId`) are never returned to the SPA
 * and so don't appear here; `lastFetchedSha` / `lastFetchedAt` are read-only
 * diagnostics that the GET response surfaces.
 */
export interface RepoComponentSettings {
  repoUrl: string;
  defaultRef?: string;
  pathInRepo: string;
  scmKind: ScmKind;
  credentialsRef?: string;
  /** Set by the discovery flow on every successful fetch. Read-only. */
  lastFetchedSha?: string;
  /** ISO timestamp of the last successful fetch. Read-only. */
  lastFetchedAt?: string;
}

export interface Component {
  id: string;
  applicationId: string;
  name: string;
  slug: string;
  role: ComponentRole;
  description: string | null;
  /**
   * Role-specific settings. For `role === 'repo'`, narrow to
   * `RepoComponentSettings`. For other roles, free-form record (typically
   * empty). The server validates the shape per role on create / patch.
   */
  settings: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateComponentInput {
  name: string;
  slug?: string;
  role: ComponentRole;
  description?: string;
  /** For role==='repo' must be a `RepoComponentSettings` (operator-supplied fields only). */
  settings?: Record<string, unknown>;
}

export interface UpdateComponentInput {
  name?: string;
  description?: string | null;
  /** Patching settings REPLACES the operator-visible fields wholesale. */
  settings?: Record<string, unknown>;
}

export interface Server {
  id: string;
  orgId: string;
  orgSlug: string;
  orgName: string;
  name: string;
  slug: string;
  hostname: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateServerInput {
  name: string;
  slug?: string;
  hostname?: string;
  description?: string;
}

export interface UpdateServerInput {
  name?: string;
  hostname?: string | null;
  description?: string | null;
}

export interface Placement {
  id: string;
  componentId: string;
  environmentId: string;
  serverId: string;
  componentSlug: string;
  componentName: string;
  componentRole: string;
  environmentSlug: string;
  environmentName: string;
  environmentKind: string;
  serverSlug: string;
  serverName: string;
  serverHostname: string | null;
  serverOrgId: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePlacementInput {
  environmentId: string;
  serverId: string;
  notes?: string;
}

export interface UpdatePlacementInput {
  serverId?: string;
  notes?: string | null;
}

export type ScriptExecutionMode = 'sync' | 'queued';
export type ScriptReversibility = 'yes' | 'no' | 'best-effort';

/**
 * Script-level environment allow-list. `slugs` and `kinds` are OR-ed at the
 * server: an env matches if its slug ∈ slugs OR its kind ∈ kinds. `null`
 * means no restriction. `EnvironmentKind` is the enum defined earlier in
 * this file (the env's own `kind` field).
 */
export interface AllowedEnvironments {
  slugs?: string[];
  kinds?: EnvironmentKind[];
}

export interface Script {
  id: string;
  componentId: string;
  /** Set on application-level listings. */
  componentSlug?: string;
  componentName?: string;
  componentRole?: string;
  applicationId?: string;
  name: string;
  slug: string;
  description: string | null;
  commandTemplate: string;
  executionMode: ScriptExecutionMode;
  expectedDurationMs: number | null;
  syncTimeoutMs: number | null;
  queuedTimeoutMs: number | null;
  reversibility: ScriptReversibility;
  warnings: string[];
  paramSchema: unknown;
  requiredPermission: string;
  /** null ⇒ no env restriction. Otherwise the script refuses runs whose env doesn't match. */
  allowedEnvironments: AllowedEnvironments | null;
  /**
   * Provenance for discovery-managed rows. Null when the row predates
   * `.deployit/` discovery (legacy SPA-typed) or was inserted by the seed.
   */
  sourceRef: { filePath: string; sha: string } | null;
  createdAt: string;
  updatedAt: string;
}

// Note: no CreateScriptInput / UpdateScriptInput here. Script create/update/
// delete is not a user-facing API surface — scripts are authored in
// `.deployit/components/<slug>/scripts.yaml` and brought in via discovery
// `/apply` (see issue #27). The SPA never POSTs/PATCHes/DELETEs scripts.

export type AccessMethodKind = 'ssh' | 'docker' | 'kubectl' | 'local';

export interface AccessMethod {
  id: string;
  orgId: string;
  orgSlug: string;
  orgName: string;
  name: string;
  slug: string;
  kind: AccessMethodKind;
  transportAccessMethodId: string | null;
  settings: Record<string, unknown>;
  credentialsRef: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AccessMethodWithChain extends AccessMethod {
  transport: AccessMethodWithChain | null;
}

export interface CreateAccessMethodInput {
  name: string;
  slug?: string;
  kind: AccessMethodKind;
  transportAccessMethodId?: string | null;
  settings?: Record<string, unknown>;
  credentialsRef?: string | null;
  description?: string;
}

export interface UpdateAccessMethodInput {
  name?: string;
  transportAccessMethodId?: string | null;
  settings?: Record<string, unknown>;
  credentialsRef?: string | null;
  description?: string | null;
}

export interface AccessMethodTestResult {
  success: boolean;
  exitCode: number | null;
  durationMs: number;
  chainSummary: string;
  resolvedCommand: string;
  stdout: string;
  stderr: string;
  errorCode?: string;
  hostFingerprint?: string;
}

export interface ScriptRunInput {
  environmentId: string;
  accessMethodId: string;
  params?: Record<string, string | number | boolean>;
}

export interface ScriptRunResult {
  success: boolean;
  exitCode: number | null;
  durationMs: number;
  chainSummary: string;
  resolvedCommand: string;
  stdout: string;
  stderr: string;
  errorCode?: string;
}

export interface Secret {
  id: string;
  orgId: string;
  orgSlug: string;
  orgName: string;
  name: string;
  pluginId: string | null;
  applicationId: string | null;
  componentId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  lastUsedAt: string | null;
}

/** Returned only by POST /secrets — contains the plaintext exactly once. */
export interface CreatedSecret extends Secret {
  plaintext: string;
  /** Set on `mode: generate-ssh-ed25519` */
  publicKey?: string;
  /** SHA-256 fingerprint; set on `mode: generate-ssh-ed25519` */
  fingerprint?: string;
}

export type CreateSecretInput =
  | {
      mode: 'paste';
      name: string;
      plaintext: string;
      metadata?: Record<string, unknown>;
      applicationId?: string;
      componentId?: string;
    }
  | {
      mode: 'generate-ssh-ed25519';
      name: string;
      comment?: string;
      metadata?: Record<string, unknown>;
      applicationId?: string;
      componentId?: string;
    };

export interface UpdateSecretInput {
  name?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Discriminated webhook-setup result. The wizard's failure UX + the
 * Components-card affordance both narrow on `status`.
 */
export type WebhookSetupResult =
  | {
      status: 'configured';
      webhookId: string;
      rotated: boolean;
      events: Array<'push' | 'release' | 'pull_request'>;
    }
  | {
      status: 'manual-fallback';
      reason: string;
      deliveryUrl: string;
      secret: string;
      events: Array<'push' | 'release' | 'pull_request'>;
    };

/* ------------- Notifications (#45) ------------- */

/**
 * In-app notification surfaced via the top-bar bell icon. `kind` is a
 * discriminator the SPA renders per-kind; v1 only emits
 * `discovery.pending`. `payload` is kind-specific structured data —
 * shape validated client-side per kind.
 */
export interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string;
  linkUrl: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
  readAt: string | null;
  dismissedAt: string | null;
}

export const api = {
  auth: {
    config: () => request<AuthConfigResponse>('GET', '/api/v1/auth/config'),

    /**
     * Discriminated response:
     *   - status === 'ok' → session cookie is set, payload matches PrincipalResponse fields
     *   - status === 'password-change-required' → no session yet; SPA must exchange the
     *     challengeToken via api.auth.passwordChange() before proceeding
     */
    login: (input: { email: string; password: string }) =>
      request<LoginResponse>('POST', '/api/v1/auth/login', input),

    passwordChange: (input: { challengeToken: string; newPassword: string }) =>
      request<PrincipalResponse>('POST', '/api/v1/auth/password-change', input),

    /* ----- 2FA / TOTP ----- */

    twoFactor: {
      status: () => request<TwoFactorStatusResponse>('GET', '/api/v1/auth/2fa/status'),

      enroll: () => request<TwoFactorEnrollResponse>('POST', '/api/v1/auth/2fa/totp/enroll'),

      activate: (input: { secret: string; code: string }) =>
        request<TwoFactorActivateResponse>('POST', '/api/v1/auth/2fa/totp/activate', input),

      disable: (input: { code?: string; password?: string }) =>
        request<{ enabled: false }>('POST', '/api/v1/auth/2fa/totp/disable', input),

      regenerateRecoveryCodes: () =>
        request<{ recoveryCodes: string[] }>(
          'POST',
          '/api/v1/auth/2fa/recovery/regenerate',
        ),

      verify: (input: { challengeToken: string; code: string }) =>
        request<PrincipalResponse>('POST', '/api/v1/auth/2fa/verify', input),
    },

    logout: () => request<void>('POST', '/api/v1/auth/logout'),

    me: () => request<PrincipalResponse>('GET', '/api/v1/auth/me'),
  },

  organisations: {
    list: () => request<Organisation[]>('GET', '/api/v1/organisations'),

    create: (input: { name: string; slug?: string; parentOrgId: string; adminEmail?: string }) =>
      request<CreateOrganisationResponse>('POST', '/api/v1/organisations', input),

    members: (orgId: string) =>
      request<OrgMember[]>('GET', `/api/v1/organisations/${encodeURIComponent(orgId)}/members`),

    pendingInvitations: (orgId: string) =>
      request<PendingInvitation[]>(
        'GET',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/invitations`,
      ),

    inviteMember: (orgId: string, input: { email: string; grantedPermissions: string[] }) =>
      request<InviteMemberResponse>(
        'POST',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/invitations`,
        input,
      ),

    ssoConfigs: (orgId: string) =>
      request<OrgSsoConfig[]>(
        'GET',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/sso-configs`,
      ),

    createSsoConfig: (orgId: string, input: CreateSsoConfigInput) =>
      request<OrgSsoConfig>(
        'POST',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/sso-configs`,
        input,
      ),

    updateSsoConfig: (orgId: string, id: string, input: UpdateSsoConfigInput) =>
      request<OrgSsoConfig>(
        'PATCH',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/sso-configs/${encodeURIComponent(id)}`,
        input,
      ),

    deleteSsoConfig: (orgId: string, id: string) =>
      request<void>(
        'DELETE',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/sso-configs/${encodeURIComponent(id)}`,
      ),

    audit: (orgId: string, query?: AuditQuery) => {
      const qs = new URLSearchParams();
      if (query?.before) qs.set('before', query.before);
      if (query?.actionPrefix) qs.set('actionPrefix', query.actionPrefix);
      if (query?.limit) qs.set('limit', String(query.limit));
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return request<AuditEntry[]>(
        'GET',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/audit${suffix}`,
      );
    },
  },

  sso: {
    /** Public — list a given org's enabled SSO providers (used by /login?sso=). */
    providersFor: (orgSlug: string) =>
      request<PublicSsoProviders>('GET', `/api/v1/auth/sso/${encodeURIComponent(orgSlug)}`),
  },

  applications: {
    listAll: () => request<Application[]>('GET', '/api/v1/applications'),

    listForOrg: (orgId: string) =>
      request<Application[]>(
        'GET',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/applications`,
      ),

    create: (orgId: string, input: CreateApplicationInput) =>
      request<Application>(
        'POST',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/applications`,
        input,
      ),

    get: (id: string) => request<Application>('GET', `/api/v1/applications/${encodeURIComponent(id)}`),

    update: (id: string, input: UpdateApplicationInput) =>
      request<Application>('PATCH', `/api/v1/applications/${encodeURIComponent(id)}`, input),

    delete: (id: string) =>
      request<void>('DELETE', `/api/v1/applications/${encodeURIComponent(id)}`),

    discover: (id: string) =>
      request<DiscoveryReport>(
        'POST',
        `/api/v1/applications/${encodeURIComponent(id)}/discover`,
      ),

    discoverApply: (id: string, selections: DiscoverySelection[]) =>
      request<DiscoveryApplyResult>(
        'POST',
        `/api/v1/applications/${encodeURIComponent(id)}/discover/apply`,
        { selections },
      ),

    /**
     * Get the active `pending_discoveries` row for an application, or
     * null when nothing is pending. Powers the yellow banner on the
     * application detail page (#45 slice 4).
     */
    pendingDiscovery: (id: string) =>
      request<PendingDiscovery | null>(
        'GET',
        `/api/v1/applications/${encodeURIComponent(id)}/pending-discovery`,
      ),
  },

  environments: {
    forApp: (appId: string) =>
      request<Environment[]>(
        'GET',
        `/api/v1/applications/${encodeURIComponent(appId)}/environments`,
      ),

    create: (appId: string, input: CreateEnvironmentInput) =>
      request<Environment>(
        'POST',
        `/api/v1/applications/${encodeURIComponent(appId)}/environments`,
        input,
      ),

    get: (id: string) =>
      request<Environment>('GET', `/api/v1/environments/${encodeURIComponent(id)}`),

    update: (id: string, input: UpdateEnvironmentInput) =>
      request<Environment>('PATCH', `/api/v1/environments/${encodeURIComponent(id)}`, input),

    delete: (id: string) =>
      request<void>('DELETE', `/api/v1/environments/${encodeURIComponent(id)}`),
  },

  components: {
    forApp: (appId: string) =>
      request<Component[]>(
        'GET',
        `/api/v1/applications/${encodeURIComponent(appId)}/components`,
      ),

    create: (appId: string, input: CreateComponentInput) =>
      request<Component>(
        'POST',
        `/api/v1/applications/${encodeURIComponent(appId)}/components`,
        input,
      ),

    get: (id: string) =>
      request<Component>('GET', `/api/v1/components/${encodeURIComponent(id)}`),

    update: (id: string, input: UpdateComponentInput) =>
      request<Component>('PATCH', `/api/v1/components/${encodeURIComponent(id)}`, input),

    delete: (id: string) =>
      request<void>('DELETE', `/api/v1/components/${encodeURIComponent(id)}`),

    /**
     * Register a webhook on the upstream SCM for a repo Component.
     * Idempotent — re-running reuses an existing hook for the same
     * deliveryUrl rather than duplicating.
     *
     * Returns a discriminated result:
     *   - `status: 'configured'` — GitHub-side hook is live.
     *   - `status: 'manual-fallback'` — upstream call failed (auth, no
     *     scope, network…) but we persisted a fresh signing secret. The
     *     SPA renders the URL + secret + paste-into-GitHub instructions.
     */
    setupWebhook: (id: string) =>
      request<WebhookSetupResult>(
        'POST',
        `/api/v1/components/${encodeURIComponent(id)}/webhook`,
      ),

    /** Tear down the webhook + clear the persisted webhookId/secret. */
    teardownWebhook: (id: string) =>
      request<void>('DELETE', `/api/v1/components/${encodeURIComponent(id)}/webhook`),
  },

  servers: {
    listAll: () => request<Server[]>('GET', '/api/v1/servers'),

    listForOrg: (orgId: string) =>
      request<Server[]>(
        'GET',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/servers`,
      ),

    create: (orgId: string, input: CreateServerInput) =>
      request<Server>(
        'POST',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/servers`,
        input,
      ),

    get: (id: string) =>
      request<Server>('GET', `/api/v1/servers/${encodeURIComponent(id)}`),

    update: (id: string, input: UpdateServerInput) =>
      request<Server>('PATCH', `/api/v1/servers/${encodeURIComponent(id)}`, input),

    delete: (id: string) =>
      request<void>('DELETE', `/api/v1/servers/${encodeURIComponent(id)}`),
  },

  placements: {
    forComponent: (componentId: string) =>
      request<Placement[]>(
        'GET',
        `/api/v1/components/${encodeURIComponent(componentId)}/placements`,
      ),

    forEnvironment: (environmentId: string) =>
      request<Placement[]>(
        'GET',
        `/api/v1/environments/${encodeURIComponent(environmentId)}/placements`,
      ),

    forApplication: (appId: string) =>
      request<Placement[]>(
        'GET',
        `/api/v1/applications/${encodeURIComponent(appId)}/placements`,
      ),

    create: (componentId: string, input: CreatePlacementInput) =>
      request<Placement>(
        'POST',
        `/api/v1/components/${encodeURIComponent(componentId)}/placements`,
        input,
      ),

    get: (id: string) =>
      request<Placement>('GET', `/api/v1/placements/${encodeURIComponent(id)}`),

    update: (id: string, input: UpdatePlacementInput) =>
      request<Placement>('PATCH', `/api/v1/placements/${encodeURIComponent(id)}`, input),

    delete: (id: string) =>
      request<void>('DELETE', `/api/v1/placements/${encodeURIComponent(id)}`),
  },

  scripts: {
    forComponent: (componentId: string) =>
      request<Script[]>('GET', `/api/v1/components/${encodeURIComponent(componentId)}/scripts`),

    forApplication: (appId: string) =>
      request<Script[]>('GET', `/api/v1/applications/${encodeURIComponent(appId)}/scripts`),

    get: (id: string) =>
      request<Script>('GET', `/api/v1/scripts/${encodeURIComponent(id)}`),

    run: (id: string, input: ScriptRunInput) =>
      request<ScriptRunResult>('POST', `/api/v1/scripts/${encodeURIComponent(id)}/run`, input),

    // No create / update / delete: scripts are authored in YAML and brought
    // in via the application's discoveryApply call. The HTTP routes were
    // removed in the chore PR that closed out #27.
  },

  accessMethods: {
    listAll: () => request<AccessMethod[]>('GET', '/api/v1/access-methods'),

    listForOrg: (orgId: string) =>
      request<AccessMethod[]>(
        'GET',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/access-methods`,
      ),

    create: (orgId: string, input: CreateAccessMethodInput) =>
      request<AccessMethod>(
        'POST',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/access-methods`,
        input,
      ),

    get: (id: string) =>
      request<AccessMethodWithChain>('GET', `/api/v1/access-methods/${encodeURIComponent(id)}`),

    update: (id: string, input: UpdateAccessMethodInput) =>
      request<AccessMethod>('PATCH', `/api/v1/access-methods/${encodeURIComponent(id)}`, input),

    delete: (id: string) =>
      request<void>('DELETE', `/api/v1/access-methods/${encodeURIComponent(id)}`),

    test: (id: string) =>
      request<AccessMethodTestResult>(
        'POST',
        `/api/v1/access-methods/${encodeURIComponent(id)}/test`,
      ),
  },

  secrets: {
    listAll: () => request<Secret[]>('GET', '/api/v1/secrets'),

    listForOrg: (orgId: string) =>
      request<Secret[]>(
        'GET',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/secrets`,
      ),

    create: (orgId: string, input: CreateSecretInput) =>
      request<CreatedSecret>(
        'POST',
        `/api/v1/organisations/${encodeURIComponent(orgId)}/secrets`,
        input,
      ),

    get: (id: string) =>
      request<Secret>('GET', `/api/v1/secrets/${encodeURIComponent(id)}`),

    update: (id: string, input: UpdateSecretInput) =>
      request<Secret>('PATCH', `/api/v1/secrets/${encodeURIComponent(id)}`, input),

    delete: (id: string) =>
      request<void>('DELETE', `/api/v1/secrets/${encodeURIComponent(id)}`),
  },

  invitations: {
    describe: (token: string) =>
      request<InvitationDescription>('GET', `/api/v1/invitations/${encodeURIComponent(token)}`),

    accept: (token: string, input: AcceptInvitationInput) =>
      request<AcceptInvitationResponse>(
        'POST',
        `/api/v1/invitations/${encodeURIComponent(token)}/accept`,
        input,
      ),

    revoke: (invitationId: string) =>
      request<void>('POST', `/api/v1/invitations/${encodeURIComponent(invitationId)}/revoke`),
  },

  account: {
    emails: () => request<AccountEmail[]>('GET', '/api/v1/account/emails'),

    setPrimary: (id: string) =>
      request<void>('POST', `/api/v1/account/emails/${encodeURIComponent(id)}/primary`),
  },

  notifications: {
    /** Most-recent N notifications for the signed-in user. Skips dismissed by default. */
    list: (opts: { limit?: number; includeDismissed?: boolean } = {}) => {
      const qs = new URLSearchParams();
      if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
      if (opts.includeDismissed) qs.set('includeDismissed', '1');
      const query = qs.toString();
      return request<Notification[]>(
        'GET',
        `/api/v1/notifications${query ? `?${query}` : ''}`,
      );
    },

    /** Bell-icon badge — `{ count: number }`. */
    unreadCount: () => request<{ count: number }>('GET', '/api/v1/notifications/unread-count'),

    /**
     * Mark one notification as read and/or dismissed. Must set at least
     * one of the two flags; server rejects an empty patch.
     */
    update: (id: string, input: { read?: true; dismissed?: true }) =>
      request<Notification>('PATCH', `/api/v1/notifications/${encodeURIComponent(id)}`, input),

    /** Convenience for the dropdown's clear-all action. */
    markAllRead: () =>
      request<{ markedRead: number }>('POST', '/api/v1/notifications/mark-all-read'),
  },

  scm: {
    /**
     * Quick repo lookup — defaultBranch + branches[] + visibility.
     * Used by the wizard's branch picker after the operator pastes
     * a PAT. Pass `accessToken` for private repos; omit for public.
     *
     * `scmKind` is optional: when omitted, the API infers from the URL
     * host (github.com → 'github', etc.). Set explicitly for hosts the
     * auto-detection doesn't recognise (enterprise hosts etc.). The
     * response echoes the kind back so the caller can persist it on
     * the Component's settings.
     */
    lookup: (input: {
      repoUrl: string;
      /** Raw PAT plaintext. Mutually exclusive with `secretId`. */
      accessToken?: string;
      /**
       * Id of an existing org-scoped Secret. Mutually exclusive with
       * `accessToken`. The server decrypts for the lookup call only.
       */
      secretId?: string;
      scmKind?: 'github' | 'gitlab' | 'bitbucket';
    }) =>
      request<{
        fullName: string;
        defaultBranch: string;
        branches: string[];
        visibility: 'public' | 'private' | 'internal';
        scmKind: 'github' | 'gitlab' | 'bitbucket';
      }>('POST', '/api/v1/scm/lookup', input),
  },
};
